import { getSessionToken } from '@shopify/app-bridge-utils';
import axios from 'axios';
import {useToast} from "@shopify/app-bridge-react";

const client = axios.create();

client.interceptors.request.use(async (config) => {
    const token = await getSessionToken(window.app);
    config.headers.Authorization = `Bearer ${token}`;

    return config;
})

client.interceptors.response.use(
    response => response,
    (error) => {
        if (error.response.status == 403) {
            alert("You don't have permission to use this.");
        } else {
            throw error;
        }
    },
);

const Api = {
    upsertCustomer: async (params) => {
        if ( params.id ) {
            return client.put('/ajax/customers/' + params.id, params);
        }

        return client.post('/ajax/customers', params);
    },

    changeCustomerStatus: async (customerId, status) => {
        return client.put(`/ajax/customers/${customerId}/status`, {
            status,
        });
    },

    listUnapprovedCustomers: async (status, page = 1) => {
        return client.get(`/ajax/customers/approval/${status}`, {
            params: { page },
        });
    },

    findCustomer: async (params) => {
        return client.get('/ajax/customers/find', {
            params: params,
        });
    },

    getCustomer: async (id) => {
        return client.get(`/ajax/customers/${id}`);
    },


    getLocations: async (customerId, page = 1, search = '') => {
        return client.get(`/ajax/customers/${customerId}/locations?page=${page}&q=${search}`);
    },

    getLocation: async (locationId) => {
        return client.get(`/ajax/locations/${locationId}`);
    },

    updateLocation: async(locationId, payload) => {
        return client.put(`/ajax/locations/${locationId}`, payload);
    },

    createLocation: async(customerId, payload) => {
        return client.post(`/ajax/customers/${customerId}/locations`, payload);
    },

    getPaymentMethods: async (id) => {
        return client.get(`/ajax/customers/${id}/payment_methods`);
    },

    getShippingMethods: async () => {
        return client.get(`/ajax/customers/shipping_methods`);
    },

    getProducts: async (tags, query, locationId) => {
        return client.get('/ajax/products', {
            params: {
                q: query,
                tags,
                per_page: 100,
                location_id: locationId,
            },
        });
    },

    getDraftOrder: async (id) => {
        return client.get(`/ajax/orders/draft/${id}`);
    },

    getOrder: async (id) => {
        return client.get(`/ajax/orders/${id}`);
    },

    placeDraftOrder: async (params) => {
        return client.post('/ajax/orders/draft', params);
    },

    chargeCreditCard: async (params) => {
        return client.post('/ajax/charges', params);
    },

    chargeOrderCreditCard: async (params) => {
        return client.post('/ajax/charges/order', params);
    },

    insertCreditCard: async (params) => {
        return client.post('/ajax/customers/' + params.id + '/payment_methods', params);
    },

    setCreditCard: async (params) => {
        let url = '/ajax/customers/' + params.id + '/payment_methods/' + params.card_id;

        if (params.type == 'location') {
            url = url + '/' + params.location_id;
        }

        return client.put(url, params);
    },

    deleteCreditCard: async (customer_id, card_id, location_id) => {
        let url = '/ajax/customers/' + customer_id + '/payment_methods/' + card_id;

        if (location_id) {
            url = url + '/' + location_id;
        }

        return client.delete(url);
    },

    unhookCreditCard: async (params) => {
        let url = '/ajax/customers/' + params.id + '/payment_methods/unhook';

        if (params.location_id) {
            url = url + '/' + params.location_id;
        }

        return client.put(url, params);
    },

    updateLocationAfterOrder: async (locationId, data) => {
        return client.put(`/ajax/locations/${locationId}/after-order`, data);
    },

    toggleCustomerOnHold: async (customerId) => {
        return client.put('/ajax/customers/' + customerId + '/toggle_on_hold', {});
    },

    getOrders: (customerId, page = 1) => {
        return client.get(`/ajax/customers/${customerId}/orders?page=${page}`);
    },

    createDiscountCode: (data) => client.post('/ajax/discount-codes', data),

    getDiscountCode: (id) => client.get(`/ajax/discount-codes/${id}`),

    updateDiscountCode: (id, data) => client.put(`/ajax/discount-codes/${id}`, data),

    listDiscountCodes: (page = 1) => client.get('/ajax/discount-codes', {
        params: { page }
    }),

    applyDiscountCode: (customerId, code, products) => client.post('/ajax/discount-codes/apply', {
        code,
        products,
        customer_id: customerId,
    }),

    hasCustomerOrders: (customerId) => client.get('/ajax/customers/' + customerId + '/has_orders'),

    deleteCustomer: async (customerId) => {
        return client.delete(`/ajax/customers/${customerId}`);
    },

    sendQuote: (data) => client.post('/ajax/orders/send-quote', data),

    deleteDiscountCode: (id) => client.delete(`/ajax/discount-codes/${id}`),

    getDiscountedLineItems: (id, page) => client.get(`/ajax/discount-codes/${id}/line-items?page=${page}`),

    findUsers: (params) => client.get('/ajax/users', {
        params: params,
    }),

    listUsers: () => client.get('/ajax/users/list'),

    getUser: (id) => client.get(`/ajax/users/${id}`),

    getMe: () => client.get(`/ajax/users/me`),

    toggleAdminPrivileges: (userId) => client.put(`/ajax/users/${userId}/toggle-admin`),

    toggleOnHoldPrivileges: (userId) => client.put(`/ajax/users/${userId}/toggle-on-hold`),

    mergeAccount: async (customerId, withAccount) => {
        return client.post(`/ajax/customers/${customerId}/merge`, {
            with_account: withAccount,
        });
    },

    transferCredit: async (locationId) => {
        return client.put(`/ajax/locations/${locationId}/credit-transfer`);
    },

    generateOpenCreditReport: async () => {
        return client.put(`/ajax/users/generate-open-credit-report`);
    },

    addUserCustomer: async (userId, customerId) => {
        return client.put(`/ajax/users/${userId}/customers/${customerId}`, {});
    },

    removeUserCustomer: async (userId, customerId) => {
        return client.delete(`/ajax/users/${userId}/customers/${customerId}`);
    },

    findUserCustomers: async (id, params) => {
        return client.get(`/ajax/users/${id}/customers`, {
            params: params,
        });
    },

    getScheduledProducts: async (params) => {
        return client.get(`/ajax/products/schedules`, {
            params: params,
        });
    },

    saveUnpublishments: async (params) => {
        return client.post(`/ajax/products/schedules`, params);
    },

    removeUnpublishments: async (id) => {
        return client.delete(`/ajax/products/schedules/${id}`);
    },

    getCustomerFile: async (id, type) => {
        return client.get(`/ajax/customers/${id}/file/${type}`);
    },

    addUserCompany: async (userId, companyId) => {
        return client.put(`/ajax/users/${userId}/companies/${companyId}`, {});
    },

    removeUserCompany: async (userId, companyId) => {
        return client.delete(`/ajax/users/${userId}/companies/${companyId}`);
    },

    findUserCompanies: async (id, params) => {
        return client.get(`/ajax/users/${id}/companies`, {
            params: params,
        });
    },

    findCompanies: async (params) => {
        return client.get(`/ajax/companies`, {
            params: params,
        });
    },

    findOrders: async (params) => {
        return client.get(`/ajax/orders`, {
            params: params,
        });
    },

    exportShipping: async (params) => {
        return client.post(`/ajax/exports/order-shipping`, params);
    },

    processOrder: async (id, params) => {
        return client.put(`/ajax/orders/${id}/process-order`, params);
    },

    mergeOrders: async (ids) => {
        return client.post(`/ajax/orders/merge`, {
            ids: ids,
        });
    },

    reserveItem: async (variantId, quantity) => {
        return client.post('/api/reservations', {
            variant_id: variantId,
            quantity,
        });
    },

    clearCart: async (items) => {
        return client.post('/api/reservations/clear', { items });
    },

    validateCart: async (items) => {
        return client.post('/api/reservations/validate', { items });
    },

    resetQuantityOnHand: async (location, product_type) => {
        return client.put('/ajax/inventory/reset-on-hand', {
            location_id: location,
            product_type
        });
    },

    getStoreLocations: async () => {
        return client.get('/ajax/inventory/locations');
    },

    resetQuantityUnshipped: async () => {
        return client.put('/ajax/inventory/reset-unshipped');
    },

    importInventory: async (params) => {
        const data = new FormData();

        for (let key in params) {
            if (params.hasOwnProperty(key)) {
                data.append(key, params[key]);
            }
        }

        return client.post('/ajax/inventory/import', data, {
            headers: { 'content-type': 'application/x-www-form-urlencoded' }
        });
    },

    getImportsList: async (type, products_type) => {
        return client.get('/ajax/inventory/import', {
            params: {
                type: type,
                products_type: products_type,
            },
        });
    },

    getDirectoriesList: async () => {
        return client.get('/ajax/images/directories');
    },

    importImages: async (directory) => {
        return client.post('/ajax/images/import', {
            directory: directory
        });
    },

    getImportLog: async (id) => {
        return client.get(`/ajax/inventory/import/${id}/log`);
    },

    cancelImport: async (id) => {
        return client.patch(`/ajax/inventory/import/${id}/cancel`);
    },

    toggleOrderSubmit: async (id) => {
        return client.put(`/ajax/orders/submit/${id}`);
    },

    toggleOrdersSubmit: async (ids) => {
        return client.put(`/ajax/orders/submit-orders`, {
            ids: ids,
        });
    },

    getConfig: async () => {
        return client.get(`/ajax/orders/config`);
    },

    updateOpf: async (orderId, order_processing_form) => {
        return client.put(`/ajax/orders/${orderId}/opf`, {
            order_processing_form: order_processing_form
        });
    },

    updateAdditionalInformation: async (orderId, data) => {
        return client.put(`/ajax/orders/${orderId}/additional`, data);
    },

    listApiUsers: async (data) => {
        return client.get(`/ajax/api-users`, data);
    },

    showApiUser: async (id, data) => {
        return client.get(`/ajax/api-users/${id}`, data);
    },

    storeApiUser: async (data) => {
        return client.post(`/ajax/api-users`, data);
    },

    updateApiUser: async (id, data) => {
        return client.put(`/ajax/api-users/${id}`, data);
    },

    refreshApiUserKey: async (id) => {
        return client.put(`/ajax/api-users/${id}/refresh-api-key`);
    },

    deleteApiUser: async (id) => {
        return client.delete(`/ajax/api-users/${id}`);
    },

    requestMoreData: async (id, data) => {
        return client.post(`/ajax/customers/${id}/request-more`, data);
    },
}

export default Api;
